import React from 'react';
import { Sparkles, ChevronDown, MapPin, Barcode, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../lib/store';
import { CURRENCIES } from '../../../../constants';
import { SmartImage } from '../../../../components/common/SmartImage';

interface LocalPriceOption {
  storeName: string;
  price: number;
  distance?: number | null;
  storeLogoUrl?: string | null;
  pricePerUnit?: number | null;
}

interface DiscoveredProduct {
  name: string;
  brand?: string | null;
  category?: string;
  size?: number | string | null;
  unit?: string | null;
  imageUrl?: string | null;
  upc?: string | null;
  localPrices: LocalPriceOption[];
}

interface LiveMarketDiscoveryCardProps {
  query?: string;
  products: DiscoveredProduct[];
  currencySymbol?: string;
}

export const LiveMarketDiscoveryCard: React.FC<{ props: LiveMarketDiscoveryCardProps; isStreaming?: boolean }> = ({
  props,
  isStreaming,
}) => {
  const { t } = useTranslation();
  const { userProfile, currency } = useStore();
  const { query, products = [], currencySymbol: propCurrencySymbol } = props || {};
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(products.length > 0 ? 0 : null);

  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find((c) => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || propCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, currency, propCurrencySymbol]);

  if (!products || products.length === 0) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center animate-pulse">
          <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('category_manager.searching', { defaultValue: 'Searching...' })}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="px-6 pt-6 pb-4 border-b border-gray-50 flex items-center gap-2">
        <Sparkles size={14} className="text-emerald-500 shrink-0" />
        <div className="min-w-0">
          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            {t('basket.quick_search_grounding', { defaultValue: 'Live Market Discovery' })}
          </h4>
          {query && <p className="text-xs font-black text-[#1a2b3c] truncate">"{query}"</p>}
        </div>
      </div>

      <div className="p-4 space-y-3 max-h-96 overflow-y-auto scrollbar-hide">
        {products.map((product, pIdx) => {
          const isExpanded = expandedIndex === pIdx;

          return (
            <div key={pIdx} className="bg-gray-50/50 rounded-2xl border border-gray-100 overflow-hidden">
              <div
                onClick={() => setExpandedIndex(isExpanded ? null : pIdx)}
                className="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-100/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0 pr-2">
                  <div className="w-9 h-9 bg-white border border-gray-100 rounded-xl overflow-hidden shrink-0 flex items-center justify-center">
                    <SmartImage
                      imageUrl={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full bg-emerald-50 text-emerald-500 flex items-center justify-center">
                          <ShoppingBag size={14} />
                        </div>
                      }
                    />
                  </div>
                  <div className="min-w-0">
                    <h5 className="text-[11px] font-black text-[#1a2b3c] break-words">{product.name}</h5>
                    <div className="flex gap-1.5 items-center mt-1 flex-wrap">
                      {product.brand && (
                        <span className="text-[7px] font-black uppercase text-gray-500 bg-gray-100 px-1 py-0.5 rounded">{product.brand}</span>
                      )}
                      {product.category && (
                        <span className="text-[7px] font-black uppercase text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded">{product.category}</span>
                      )}
                      {product.size && (
                        <span className="text-[7px] font-bold text-gray-400">
                          {product.size} {product.unit}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="border-t border-gray-100 bg-white px-3 pb-3 pt-2 space-y-2 overflow-hidden"
                  >
                    <div className="flex items-center justify-between text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      <span>{t('basket.local_store_prices', { defaultValue: 'Local Offers' })}</span>
                      {product.upc && (
                        <span className="flex items-center gap-0.5">
                          <Barcode size={8} />
                          {product.upc}
                        </span>
                      )}
                    </div>

                    {product.localPrices.map((option, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50/50 rounded-xl p-2 border border-gray-100">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-6 h-6 bg-emerald-50 rounded-lg text-emerald-600 overflow-hidden flex items-center justify-center shrink-0">
                            {option.storeLogoUrl ? (
                              <SmartImage imageUrl={option.storeLogoUrl} className="w-full h-full object-contain" />
                            ) : (
                              <MapPin size={10} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[9px] font-black text-[#1a2b3c] break-words">{option.storeName}</p>
                            {option.distance != null && (
                              <p className="text-[7px] font-bold text-gray-400">
                                {option.distance.toFixed(1)} {t('basket.miles_away', { defaultValue: 'mi away' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end leading-none shrink-0">
                          <span className="text-[10px] font-black text-[#1a2b3c]">
                            {activeCurrencySymbol}
                            {option.price.toFixed(2)}
                          </span>
                          {option.pricePerUnit != null && product.unit && (
                            <span className="text-[7px] font-bold text-gray-400 mt-0.5">
                              {activeCurrencySymbol}
                              {option.pricePerUnit.toFixed(2)}/{product.unit}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
